import { z } from "zod";
import { IsoDate } from "./common.js";
export const GENERIC_TABLE_LIMITS = {
    columns: 40,
    rows: 500,
    populatedCells: 5_000,
    textCellCharacters: 4_000,
    labelCharacters: 255,
    options: 100,
    userDisplayNameCharacters: 255,
    totalTextCharacters: 1_000_000,
};
const TableId = z.string().min(1).max(128);
const TableLabel = z.string().min(1).max(GENERIC_TABLE_LIMITS.labelCharacters);
export const GenericTableBindingSchema = z
    .object({
    templateId: z.string().min(1).max(128),
    templateVersion: z.number().int().positive(),
    skillId: z.string().min(3).max(128),
})
    .strict();
export const GenericTableColumnTypeSchema = z.enum([
    "text",
    "longText",
    "number",
    "singleSelect",
    "multiSelect",
    "date",
    "user",
    "boolean",
]);
const GenericTableOptionSchema = z
    .object({ id: TableId, label: TableLabel })
    .strict();
export const GenericTableColumnSchema = z
    .object({
    id: TableId,
    label: TableLabel,
    type: GenericTableColumnTypeSchema,
    origin: z.enum(["template", "user"]),
    required: z.boolean().optional(),
    width: z.enum(["small", "medium", "large"]).optional(),
    unit: z.string().max(64).optional(),
    options: z
        .array(GenericTableOptionSchema)
        .max(GENERIC_TABLE_LIMITS.options)
        .optional(),
})
    .strict()
    .superRefine((column, ctx) => {
    const options = column.options ?? [];
    if (!["singleSelect", "multiSelect"].includes(column.type) &&
        options.length > 0) {
        ctx.addIssue({
            code: "custom",
            message: "options are valid only for select columns",
            path: ["options"],
        });
    }
    if (new Set(options.map((option) => option.id)).size !== options.length) {
        ctx.addIssue({
            code: "custom",
            message: "option IDs must be unique within a column",
            path: ["options"],
        });
    }
});
export const GenericTableCellSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("empty") }).strict(),
    z
        .object({
        type: z.literal("text"),
        value: z.string().max(GENERIC_TABLE_LIMITS.textCellCharacters),
    })
        .strict(),
    z
        .object({
        type: z.literal("longText"),
        value: z.string().max(GENERIC_TABLE_LIMITS.textCellCharacters),
    })
        .strict(),
    z.object({ type: z.literal("number"), value: z.number().finite() }).strict(),
    z.object({ type: z.literal("singleSelect"), optionId: TableId }).strict(),
    z
        .object({
        type: z.literal("multiSelect"),
        optionIds: z.array(TableId).max(GENERIC_TABLE_LIMITS.options),
    })
        .strict(),
    z.object({ type: z.literal("date"), value: IsoDate }).strict(),
    z
        .object({
        type: z.literal("user"),
        userId: z
            .string()
            .min(1)
            .max(GENERIC_TABLE_LIMITS.userDisplayNameCharacters)
            .nullable(),
        displayName: z
            .string()
            .min(1)
            .max(GENERIC_TABLE_LIMITS.userDisplayNameCharacters),
    })
        .strict(),
    z.object({ type: z.literal("boolean"), value: z.boolean() }).strict(),
]);
const GenericTableCellsSchema = z
    .record(GenericTableCellSchema)
    .superRefine((cells, ctx) => {
    if (Object.keys(cells).length > GENERIC_TABLE_LIMITS.columns) {
        ctx.addIssue({
            code: "custom",
            message: `row cells cannot exceed ${GENERIC_TABLE_LIMITS.columns}`,
        });
    }
});
export const GenericTableRowSchema = z
    .object({
    id: TableId,
    origin: z.enum(["template", "user"]),
    sourceKey: z.string().min(1).max(255).optional(),
    cells: GenericTableCellsSchema,
})
    .strict();
export const GenericTableParamsSchema = z
    .object({
    schemaVersion: z.literal(1),
    title: z.string().max(255),
    description: z.string().max(2_000),
    binding: GenericTableBindingSchema.nullable(),
    columns: z
        .array(GenericTableColumnSchema)
        .max(GENERIC_TABLE_LIMITS.columns),
    rows: z.array(GenericTableRowSchema).max(GENERIC_TABLE_LIMITS.rows),
})
    .strict()
    .superRefine((table, ctx) => {
    const columnIds = table.columns.map((column) => column.id);
    const rowIds = table.rows.map((row) => row.id);
    if (new Set(columnIds).size !== columnIds.length) {
        ctx.addIssue({
            code: "custom",
            message: "column IDs must be unique",
            path: ["columns"],
        });
    }
    if (new Set(rowIds).size !== rowIds.length) {
        ctx.addIssue({
            code: "custom",
            message: "row IDs must be unique",
            path: ["rows"],
        });
    }
    const sourceKeys = table.rows.flatMap((row) => row.sourceKey ? [row.sourceKey] : []);
    if (new Set(sourceKeys).size !== sourceKeys.length) {
        ctx.addIssue({
            code: "custom",
            message: "row source keys must be unique",
            path: ["rows"],
        });
    }
    const byColumn = new Map(table.columns.map((column) => [column.id, column]));
    const optionIdsByColumn = new Map(table.columns.map((column) => [
        column.id,
        new Set((column.options ?? []).map((option) => option.id)),
    ]));
    let populatedCells = 0;
    let totalTextCharacters = table.title.length + table.description.length;
    if (table.binding) {
        totalTextCharacters +=
            table.binding.templateId.length + table.binding.skillId.length;
    }
    for (const column of table.columns) {
        totalTextCharacters +=
            column.id.length + column.label.length + (column.unit?.length ?? 0);
        for (const option of column.options ?? []) {
            totalTextCharacters += option.id.length + option.label.length;
        }
    }
    table.rows.forEach((row, rowIndex) => {
        totalTextCharacters += row.id.length + (row.sourceKey?.length ?? 0);
        for (const [columnId, cell] of Object.entries(row.cells)) {
            const column = byColumn.get(columnId);
            if (!column) {
                ctx.addIssue({
                    code: "custom",
                    message: `cell references unknown column ${columnId}`,
                    path: ["rows", rowIndex, "cells", columnId],
                });
                continue;
            }
            if (cell.type !== "empty")
                populatedCells += 1;
            if (cell.type !== "empty" && cell.type !== column.type) {
                ctx.addIssue({
                    code: "custom",
                    message: `cell type must match ${column.type} column`,
                    path: ["rows", rowIndex, "cells", columnId],
                });
            }
            if (cell.type === "text" || cell.type === "longText") {
                totalTextCharacters += cell.value.length;
            }
            if (cell.type === "date")
                totalTextCharacters += cell.value.length;
            if (cell.type === "user") {
                totalTextCharacters +=
                    cell.displayName.length + (cell.userId?.length ?? 0);
            }
            if (cell.type === "singleSelect") {
                totalTextCharacters += cell.optionId.length;
            }
            if (cell.type === "multiSelect") {
                totalTextCharacters += cell.optionIds.reduce((total, optionId) => total + optionId.length, 0);
                if (new Set(cell.optionIds).size !== cell.optionIds.length) {
                    ctx.addIssue({
                        code: "custom",
                        message: "selected option IDs must be unique",
                        path: ["rows", rowIndex, "cells", columnId],
                    });
                }
            }
            const optionIds = optionIdsByColumn.get(columnId);
            if (cell.type === "singleSelect" && !optionIds.has(cell.optionId)) {
                ctx.addIssue({
                    code: "custom",
                    message: "selected option is not declared by the column",
                    path: ["rows", rowIndex, "cells", columnId],
                });
            }
            if (cell.type === "multiSelect" &&
                cell.optionIds.some((optionId) => !optionIds.has(optionId))) {
                ctx.addIssue({
                    code: "custom",
                    message: "one or more selected options are not declared by the column",
                    path: ["rows", rowIndex, "cells", columnId],
                });
            }
        }
    });
    if (populatedCells > GENERIC_TABLE_LIMITS.populatedCells) {
        ctx.addIssue({
            code: "custom",
            message: `table cannot exceed ${GENERIC_TABLE_LIMITS.populatedCells} populated cells`,
            path: ["rows"],
        });
    }
    if (totalTextCharacters > GENERIC_TABLE_LIMITS.totalTextCharacters) {
        ctx.addIssue({
            code: "custom",
            message: "table text exceeds the widget character limit",
            path: ["rows"],
        });
    }
});
export const defaultGenericTableParams = {
    schemaVersion: 1,
    title: "Structured Table",
    description: "",
    binding: null,
    columns: [],
    rows: [],
};
//# sourceMappingURL=genericTable.js.map